<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { QuillEditor } from '@vueup/vue-quill';
import { ref, onMounted, reactive, watch, computed } from "vue";
import { useForm, Link } from '@inertiajs/vue3';
import Loading from 'vue-loading-overlay';
import FlatPickr from 'vue-flatpickr-component';

const props = defineProps({
    year: String,
    allHotels: Object,
    countries: Object,
    agents: Object,
    flash: Object,
    hotelData: Object,
    user: Object,
});
let isLoading = ref(false);

const configDept = ref({
    altFormat: 'J M, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
});
const configArrv = ref({
    altFormat: 'J M, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
});

// Hotel information array
const hotelIncluded = ref('yes');
const numberOfHotels = ref(1);
const hotelArray = reactive([
    {
        hotel_name: '',
        meal_type: '',
        check_in: '',
        check_out: '',
        nights: '',
        itinerary: [{ day: 1, description: '' }]
    }
]);

// Move the BookingAddform declaration before the watch function
const BookingAddform = useForm({
    stay_type: '0',
    booking_no: '',
    hotel_included: 'yes',
    number_of_hotels: 1,
    hotels: hotelArray,
    sight_seeing: '',
    car_used: '',
    agent_id: '',
    arrival_date: '',
    departure_date: '',
    arrival_flight: '',
    departure_flight: '',
    payment_deadline: '',
    payment_details: '',
    remarks: '',
    number_of_adults: 1,
    number_of_children: 0,
    guest_name: '',
});

// Watch for stay type changes to adjust hotel options
watch(() => BookingAddform.stay_type, (newValue) => {
    if (newValue === '0') { // Single stay
        numberOfHotels.value = 1;
        updateHotelArray();
    }
});

// Watch for number of hotels changes
watch(numberOfHotels, (newValue) => {
    updateHotelArray();
});

// Update hotel array based on number of hotels
function updateHotelArray() {
    const currentLength = hotelArray.length;
    const targetLength = hotelIncluded.value === 'yes' ? parseInt(numberOfHotels.value) : 0;

    if (currentLength < targetLength) {
        // Add more hotels
        for (let i = currentLength; i < targetLength; i++) {
            hotelArray.push({
                hotel_name: '',
                meal_type: '',
                check_in: '',
                check_out: '',
                nights: '',
                itinerary: [{ day: 1, description: '' }]
            });
        }
    } else if (currentLength > targetLength) {
        // Remove excess hotels
        hotelArray.splice(targetLength, currentLength - targetLength);
    }
}

// Add a computed property for overall dates
const overallDates = computed(() => {
    if (hotelIncluded.value === 'no' || hotelArray.length === 0) {
        return {
            firstCheckIn: BookingAddform.arrival_date,
            lastCheckOut: BookingAddform.departure_date
        };
    }

    // Find earliest check-in and latest check-out from hotels
    let firstCheckIn = null;
    let lastCheckOut = null;

    hotelArray.forEach(hotel => {
        if (hotel.check_in && (!firstCheckIn || new Date(hotel.check_in) < new Date(firstCheckIn))) {
            firstCheckIn = hotel.check_in;
        }

        if (hotel.check_out && (!lastCheckOut || new Date(hotel.check_out) > new Date(lastCheckOut))) {
            lastCheckOut = hotel.check_out;
        }
    });

    return {
        firstCheckIn,
        lastCheckOut
    };
});

// Update BookingAddform with overall dates when they change
watch(overallDates, (newDates) => {
    if (hotelIncluded.value === 'yes' && hotelArray.length > 0) {
        BookingAddform.arrival_date = newDates.firstCheckIn;
        BookingAddform.departure_date = newDates.lastCheckOut;
    }
});

// Validate hotel dates to ensure proper sequencing
function validateHotelDates(hotelIndex) {
    if (hotelArray.length <= 1) return true;

    const currentHotel = hotelArray[hotelIndex];

    // If this is not the first hotel, check that check-in is after previous hotel's check-out
    if (hotelIndex > 0) {
        const prevHotel = hotelArray[hotelIndex - 1];
        if (prevHotel.check_out && currentHotel.check_in) {
            const prevCheckOut = new Date(prevHotel.check_out);
            const currentCheckIn = new Date(currentHotel.check_in);

            if (currentCheckIn < prevCheckOut) {
                alert(`Hotel ${hotelIndex + 1} check-in date must be on or after Hotel ${hotelIndex}'s check-out date.`);
                // Reset the check-in date to the previous hotel's check-out date
                currentHotel.check_in = prevHotel.check_out;
                return false;
            }
        }
    }

    // If this is not the last hotel, check that check-out is before next hotel's check-in
    if (hotelIndex < hotelArray.length - 1) {
        const nextHotel = hotelArray[hotelIndex + 1];
        if (nextHotel.check_in && currentHotel.check_out) {
            const nextCheckIn = new Date(nextHotel.check_in);
            const currentCheckOut = new Date(currentHotel.check_out);

            if (currentCheckOut > nextCheckIn) {
                alert(`Hotel ${hotelIndex + 1} check-out date must be on or before Hotel ${hotelIndex + 2}'s check-in date.`);
                // Reset the check-out date to the next hotel's check-in date
                currentHotel.check_out = nextHotel.check_in;
                return false;
            }
        }
    }

    return true;
}

// Update the calculateNights function to include validation
function calculateNights(index) {
    // First validate the dates
    if (!validateHotelDates(index)) {
        // If validation fails, recalculate after correction
        setTimeout(() => calculateNights(index), 100);
        return;
    }

    const checkIn = new Date(hotelArray[index].check_in);
    const checkOut = new Date(hotelArray[index].check_out);

    if (!isNaN(checkIn.getTime()) && !isNaN(checkOut.getTime())) {
        const diffTime = Math.abs(checkOut - checkIn);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        hotelArray[index].nights = diffDays;

        // Update itinerary days
        updateItineraryDays(index, diffDays);
    }
}

// Update itinerary days based on nights
function updateItineraryDays(index, nights) {
    const currentLength = hotelArray[index].itinerary.length;

    if (currentLength < nights) {
        // Add more days
        for (let i = currentLength; i < nights; i++) {
            hotelArray[index].itinerary.push({ day: i + 1, description: '' });
        }
    } else if (currentLength > nights) {
        // Remove excess days
        hotelArray[index].itinerary.splice(nights, currentLength - nights);
    }
}

// Add itinerary day
function addItineraryDay(hotelIndex) {
    const nextDay = hotelArray[hotelIndex].itinerary.length + 1;
    hotelArray[hotelIndex].itinerary.push({ day: nextDay, description: '' });
}

// Remove itinerary day
function removeItineraryDay(hotelIndex, dayIndex) {
    if (hotelArray[hotelIndex].itinerary.length > 1) {
        hotelArray[hotelIndex].itinerary.splice(dayIndex, 1);

        // Renumber days
        hotelArray[hotelIndex].itinerary.forEach((item, idx) => {
            item.day = idx + 1;
        });
    }
}

onMounted(() => {
    const readonlyInput = document.querySelectorAll('input[readonly]');
    readonlyInput.forEach((item) => {
        item.style.backgroundColor = '#eceef14a';
    });
});

function handleDeptDateChange() {
    configDept.value = {
        ...configDept.value,
        minDate: BookingAddform.arrival_date
    };
}

const BookingAddformSubmit = () => {
    // Prepare the form data
    BookingAddform.hotels = hotelArray;

    // Set overall dates from hotels if hotels are included
    if (hotelIncluded.value === 'yes' && hotelArray.length > 0) {
        BookingAddform.arrival_date = overallDates.value.firstCheckIn;
        BookingAddform.departure_date = overallDates.value.lastCheckOut;
    }

    BookingAddform.post('/other-bookings/store', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
};

</script>
<style scoped>
@import "../../../../public/ui/vendor/css/vue-quill.snow.css";
@import "vue-loading-overlay/dist/vue-loading.css";
@import "@vueform/multiselect/themes/default.css";
@import 'flatpickr/dist/themes/material_orange.css';
</style>
<template>
    <AppLayout title="Add New Booking" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="card mb-4">
                <h5 class="card-header">Create Other Destination Booking</h5>
                <form class="card-body" @submit.prevent="BookingAddformSubmit">
                    <h6 class="text-primary">Basic Information</h6>
                    <div class="row g-3">
                        <div class="col-md-4">
                            <label for="stay_type" class="form-label"><b>Stay Type</b></label>
                            <select class="form-select" name="stay_type" v-model="BookingAddform.stay_type">
                                <option value="0">Single Stay</option>
                                <option value="1">Split Stay</option>
                            </select>
                        </div>
                        <div class="col-md-4">
                            <label for="booking_no_id" class="form-label"><b>Booking Number</b></label>
                            <input type="text" class="form-control" name="booking_no"
                                v-model="BookingAddform.booking_no" placeholder="Auto-generated" readonly />
                        </div>
                        <div class="col-md-4">
                            <label for="hotel_included" class="form-label"><b>Hotels Included</b></label>
                            <select class="form-select" name="hotel_included" v-model="hotelIncluded">
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>

                        <!-- Show number of hotels selection only for split stay and when hotels are included -->
                        <div class="col-md-4" v-if="hotelIncluded === 'yes' && BookingAddform.stay_type === '1'">
                            <label for="number_of_hotels" class="form-label"><b>Number of Hotels</b></label>
                            <select class="form-select" name="number_of_hotels" v-model="numberOfHotels">
                                <option v-for="n in 5" :key="n" :value="n">{{ n }}</option>
                            </select>
                        </div>
                    </div>

                    <!-- Hotel Information Section -->
                    <div v-if="hotelIncluded === 'yes'">
                        <hr class="my-4 mx-n4">
                        <h6 class="text-primary">Hotel Information</h6>

                        <div v-for="(hotel, hotelIndex) in hotelArray" :key="hotelIndex" class="mb-4 p-3"
                            style="border: 1px solid #eee; border-radius: 5px;">
                            <h6 class="mb-3">Hotel {{ hotelIndex + 1 }}</h6>
                            <div class="row g-3">
                                <div class="col-md-6">
                                    <label :for="'hotel_name_' + hotelIndex" class="form-label"><b>Hotel
                                            Name</b></label>
                                    <input type="text" class="form-control" :name="'hotel_name_' + hotelIndex"
                                        v-model="hotel.hotel_name" placeholder="Enter hotel name" />
                                </div>
                                <div class="col-md-6">
                                    <label :for="'meal_type_' + hotelIndex" class="form-label"><b>Meal Type</b></label>
                                    <input type="text" class="form-control" :name="'meal_type_' + hotelIndex"
                                        v-model="hotel.meal_type" placeholder="Enter meal type" />
                                </div>
                                <div class="col-md-3">
                                    <label :for="'check_in_' + hotelIndex" class="form-label"><b>Check In</b></label>
                                    <FlatPickr v-model="hotel.check_in" :config="configArrv" class="form-control"
                                        placeholder="Check in Date" :name="'check_in_' + hotelIndex"
                                        @update:model-value="() => calculateNights(hotelIndex)" />
                                </div>
                                <div class="col-md-3">
                                    <label :for="'check_out_' + hotelIndex" class="form-label"><b>Check Out</b></label>
                                    <FlatPickr v-model="hotel.check_out" :config="configDept" class="form-control"
                                        placeholder="Check out Date" :name="'check_out_' + hotelIndex"
                                        @update:model-value="() => calculateNights(hotelIndex)" />
                                </div>
                                <div class="col-md-3">
                                    <label :for="'nights_' + hotelIndex" class="form-label"><b>Nights</b></label>
                                    <input type="number" class="form-control" :name="'nights_' + hotelIndex"
                                        v-model="hotel.nights" placeholder="Calculated" readonly />
                                </div>
                            </div>

                            <!-- Itinerary Section -->
                            <div class="mt-4">
                                <h6 class="mb-3">Itinerary</h6>
                                <div v-for="(day, dayIndex) in hotel.itinerary" :key="dayIndex" class="row g-3 mb-2">
                                    <div class="col-md-2">
                                        <label :for="'day_' + hotelIndex + '_' + dayIndex" class="form-label"><b>Day {{
                                            day.day }}</b></label>
                                    </div>
                                    <div class="col-md-8">
                                        <textarea class="form-control"
                                            :name="'day_description_' + hotelIndex + '_' + dayIndex"
                                            v-model="day.description" placeholder="Enter day activities"
                                            rows="2"></textarea>
                                    </div>
                                    <div class="col-md-2">
                                        <button type="button" class="btn btn-danger btn-sm"
                                            @click="removeItineraryDay(hotelIndex, dayIndex)">
                                            <i class="tf-icons bx bx-minus"></i>
                                        </button>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-12">
                                        <button type="button" class="btn btn-primary btn-sm"
                                            @click="addItineraryDay(hotelIndex)">
                                            <i class="tf-icons bx bx-plus"></i> Add Day
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Guest Information</h6>
                    <div class="row g-3">
                        <div class="col-md-6">
                            <label for="guest_name" class="form-label"><b>Guest Name</b></label>
                            <input type="text" class="form-control" name="guest_name"
                                v-model="BookingAddform.guest_name" placeholder="Enter guest name" required />
                            <div v-if="BookingAddform.errors.guest_name" class="text-danger mt-1">
                                {{ BookingAddform.errors.guest_name }}
                            </div>
                        </div>
                        <div class="col-md-3">
                            <label for="number_of_adults" class="form-label"><b>Number of Adults</b></label>
                            <input type="number" min="1" class="form-control" name="number_of_adults"
                                v-model="BookingAddform.number_of_adults" required />
                            <div v-if="BookingAddform.errors.number_of_adults" class="text-danger mt-1">
                                {{ BookingAddform.errors.number_of_adults }}
                            </div>
                        </div>
                        <div class="col-md-3">
                            <label for="number_of_children" class="form-label"><b>Number of Children</b></label>
                            <input type="number" min="0" class="form-control" name="number_of_children"
                                v-model="BookingAddform.number_of_children" />
                            <div v-if="BookingAddform.errors.number_of_children" class="text-danger mt-1">
                                {{ BookingAddform.errors.number_of_children }}
                            </div>
                        </div>
                    </div>

                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Additional Information</h6>
                    <div class="row g-3">
                        <div class="col-md-12">
                            <label for="sight_seeing" class="form-label"><b>Sight Seeing</b></label>
                            <textarea class="form-control" name="sight_seeing" v-model="BookingAddform.sight_seeing"
                                placeholder="Enter sight seeing details" rows="3"></textarea>
                        </div>
                        <div class="col-md-12">
                            <label for="car_used" class="form-label"><b>Car Used</b></label>
                            <input type="text" class="form-control" name="car_used" v-model="BookingAddform.car_used"
                                placeholder="Enter car details" />
                        </div>
                        <div class="col-md-4">
                            <label for="agent_id" class="form-label"><b>Agent</b></label>
                            <select class="form-select" name="agent_id" v-model="BookingAddform.agent_id"
                                required="required">
                                <option value="" disabled="disabled" selected="selected">Type an agent name...</option>
                                <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                                    {{ agent.agency_name }}</option>
                            </select>
                        </div>
                    </div>

                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Travel Information</h6>
                    <div class="row g-3">
                        <!-- Only show overall check-in/check-out fields when hotels are not included -->
                        <template v-if="hotelIncluded === 'no'">
                            <div class="col-md-3">
                                <label for="arrival_date" class="form-label"><b>Overall Check in</b></label>
                                <FlatPickr v-model="BookingAddform.arrival_date" :config="configArrv"
                                    class="form-control" placeholder="Check in Date" name="arrival_date"
                                    @change="handleDeptDateChange"
                                    :class="{ 'is-invalid': BookingAddform.errors.arrival_date }" />
                                <div v-if="BookingAddform.errors.arrival_date" class="invalid-feedback">
                                    <div v-text="BookingAddform.errors.arrival_date"></div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <label for="departure_date" class="form-label"><b>Overall Check out</b></label>
                                <FlatPickr v-model="BookingAddform.departure_date" :config="configDept"
                                    class="form-control" placeholder="Check out Date" name="departure_date"
                                    :class="{ 'is-invalid': BookingAddform.errors.departure_date }" />
                                <div v-if="BookingAddform.errors.departure_date" class="invalid-feedback">
                                    <div v-text="BookingAddform.errors.departure_date"></div>
                                </div>
                            </div>
                        </template>

                        <!-- When hotels are included, show the derived overall dates as read-only -->
                        <template v-else>
                            <div class="col-md-3">
                                <label class="form-label"><b>First Check-in</b></label>
                                <input type="text" class="form-control" :value="overallDates.firstCheckIn" readonly />
                                <small class="text-muted">First hotel's check-in date</small>
                            </div>
                            <div class="col-md-3">
                                <label class="form-label"><b>Last Check-out</b></label>
                                <input type="text" class="form-control" :value="overallDates.lastCheckOut" readonly />
                                <small class="text-muted">Last hotel's check-out date</small>
                            </div>
                        </template>

                        <div class="col-md-6">
                            <label for="arrival_flight" class="form-label"><b>Arrival Flight / Info</b></label>
                            <input type="text" class="form-control" placeholder="Arrival Flight or Info"
                                name="arrival_flight" v-model="BookingAddform.arrival_flight" />
                        </div>
                        <div class="col-md-6">
                            <label for="departure_flight" class="form-label"><b>Departure Flight / Info</b></label>
                            <input type="text" class="form-control" placeholder="Departure Flight or Info"
                                name="departure_flight" v-model="BookingAddform.departure_flight" />
                        </div>
                    </div>

                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Payment Information</h6>
                    <div class="row g-3">
                        <div class="col-md-4">
                            <label for="payment_deadline" class="form-label"><b>Payment/Cancellation
                                    Deadline</b></label>
                            <FlatPickr v-model="BookingAddform.payment_deadline" :config="configArrv"
                                class="form-control" placeholder="Payment Deadline" name="payment_deadline" />
                        </div>
                        <div class="col-md-8">
                            <label for="payment_details" class="form-label"><b>Payment Details</b></label>
                            <textarea class="form-control" name="payment_details"
                                v-model="BookingAddform.payment_details" placeholder="Enter payment details"
                                rows="3"></textarea>
                        </div>
                    </div>

                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Other Information</h6>
                    <div class="row g-3">
                        <div class="col-md-12">
                            <label for="description" class="form-label"><b>Remarks</b></label>
                            <QuillEditor theme="snow" placeholder="    Type extra remarks"
                                v-model:content="BookingAddform.remarks" contentType="html" toolbar="essential"
                                object="true" :searchable="true" name="remarks" />
                        </div>
                    </div>
                    <div class="modal-footer border-0 mt-3">
                        <Link type="button" class="btn btn-label-secondary" :href="'/hotels'">
                        Cancel
                        </Link>
                        <button type="submit" class="btn btn-primary me-sm-3 me-1"
                            :disabled="BookingAddform.processing">Create
                            Booking</button>
                    </div>
                </form>
            </div>
        </div>
    </AppLayout>
</template>